import { useEffect, useState, useRef } from "react";
import { useParams, Link, Navigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, FileText, Maximize2, ExternalLink, X, ChevronLeft, ChevronRight, Link2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Document, Page, pdfjs } from "react-pdf";
import { WhatsAppIcon } from "../components/WhatsAppIcon";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { useI18n } from "../context/I18nContext";
import { getContent } from "../i18n/data";
import { useSeo } from "../lib/seo";
import { api, mediaUrl } from "../lib/api";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const ease = [0.16, 1, 0.3, 1];

export default function BrandPage() {
  const { key } = useParams();
  const { t, lang } = useI18n();
  const { brands } = getContent(lang);
  const b = brands[key];

  useSeo({
    title: b ? `${b.name} — ${b.tagline} · OMNIA` : "OMNIA",
    description: b?.description,
    image: b?.image,
    type: "website",
  });

  if (!b) return <Navigate to="/" replace />;

  return (
    <div className="bg-cream text-navy overflow-x-hidden">
      <Navbar />

      <section className="pt-36 pb-16 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <Link to="/#ecosistema" className="inline-flex items-center gap-2 text-sm text-navy/50 hover:text-navy transition-colors mb-12">
            <ArrowLeft className="w-4 h-4" /> {t("brand.back")}
          </Link>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }}>
            <div className="overline mb-6" style={{ color: b.accent }}>{b.role}</div>
            <h1 className="font-editorial text-7xl md:text-8xl tracking-tight leading-[0.9]">{b.name}</h1>
            <p className="mt-4 text-xl text-navy/50">{b.tagline}</p>
            <p className="mt-8 max-w-2xl text-lg text-navy/60 leading-relaxed">{b.description}</p>
          </motion.div>
        </div>
      </section>

      <section className="px-6 lg:px-10 mb-8">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.9, ease }} className="aspect-[21/9] overflow-hidden bg-navy">
            <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="overline text-navy/40 mb-12">{t("brand.capabilities")}</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10 border border-navy/10">
            {b.capabilities.map((c, i) => (
              <motion.div key={c.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, ease, delay: (i % 3) * 0.08 }}
                className="bg-cream p-8 md:p-10 hover:bg-white transition-colors duration-500">
                <div className="font-mono text-sm mb-6" style={{ color: b.accent }}>0{i + 1}</div>
                <h3 className="text-2xl font-medium tracking-tight mb-3">{c.title}</h3>
                <p className="text-sm text-navy/60 leading-relaxed">{c.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {key === "aura" && <BrandPresentations accent={b.accent} lang={lang} />}

      <section className="pb-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto bg-navy text-cream grain rounded-lg p-12 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-editorial text-4xl md:text-5xl leading-tight max-w-xl">{t("brand.ctaLine")(b.name)}</h2>
          <Link to="/login" data-testid={`brand-cta-${b.key}`}
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white px-7 py-4 hover:opacity-90 transition-opacity whitespace-nowrap">
            {t("brand.cta")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function BrandPresentations({ accent, lang }) {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [full, setFull] = useState(false);
  const [params] = useSearchParams();
  const sectionRef = useRef(null);
  useEffect(() => {
    api.get("/aura/public/presentations")
      .then((r) => {
        setItems(r.data);
        const target = params.get("presentacion") && r.data.find((p) => p.id === params.get("presentacion"));
        setSel(target || r.data[0] || null);
        if (target) setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth" }), 400);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    document.body.style.overflow = full ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [full]);
  if (!items.length) return null;
  const url = sel ? mediaUrl(sel.url) : "";
  const shareUrl = sel ? `${window.location.origin}/marca/aura?presentacion=${sel.id}` : "";
  const shareText = sel ? `${sel.title} — ${shareUrl}` : "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success(lang === "en" ? "Link copied" : "Enlace copiado"); }
    catch { toast.error(lang === "en" ? "Could not copy" : "No se pudo copiar"); }
  };
  return (
    <section ref={sectionRef} className="py-16 md:py-20 px-6 lg:px-10" data-testid="aura-presentations-section">
      <div className="max-w-7xl mx-auto">
        <div className="overline text-navy/40 mb-8">{lang === "en" ? "Presentations / Catalogs" : "Presentaciones / Catálogos"}</div>
        <div className="flex flex-wrap gap-4 mb-8">
          {items.map((p) => (
            <button key={p.id} data-testid={`aura-pres-${p.id}`} onClick={() => setSel(p)}
              className="group w-44 md:w-56 text-left rounded-lg overflow-hidden border bg-white transition-all duration-300"
              style={sel?.id === p.id
                ? { borderColor: accent, boxShadow: `0 0 0 2px ${accent}` }
                : { borderColor: "rgba(11,18,52,0.12)" }}>
              <div className="aspect-[16/10] bg-navy/[0.04] overflow-hidden flex items-center justify-center">
                {p.cover
                  ? <img src={mediaUrl(p.cover)} alt={p.title} loading="lazy"
                      className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500" />
                  : <FileText className="w-8 h-8 text-navy/25" />}
              </div>
              <div className="px-3 py-2.5 text-sm font-medium truncate flex items-center gap-1.5"
                style={sel?.id === p.id ? { color: accent } : {}}>
                <FileText className="w-3.5 h-3.5 shrink-0 opacity-60" /> {p.title}
              </div>
            </button>
          ))}
        </div>
        {sel && (
          <motion.div key={sel.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}
            className="border border-navy/10 rounded-lg overflow-hidden bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-navy/10">
              <div className="text-sm font-medium truncate">{sel.title}</div>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={copyLink} data-testid="aura-pres-copy" title={lang === "en" ? "Copy link" : "Copiar enlace"}
                  className="text-navy/40 hover:text-navy transition-colors"><Link2 className="w-4 h-4" /></button>
                <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer"
                  data-testid="aura-pres-wa" title="WhatsApp" className="text-navy/40 hover:text-[#25D366] transition-colors">
                  <WhatsAppIcon className="w-4 h-4" /></a>
                <a href={`mailto:?subject=${encodeURIComponent(sel.title)}&body=${encodeURIComponent(shareText)}`}
                  data-testid="aura-pres-mail" title={lang === "en" ? "Send by email" : "Enviar por correo"}
                  className="text-navy/40 hover:text-navy transition-colors"><Mail className="w-4 h-4" /></a>
                <span className="w-px h-4 bg-navy/10" />
                <button onClick={() => setFull(true)} data-testid="aura-pres-fullscreen" title={lang === "en" ? "Fullscreen" : "Pantalla completa"}
                  className="text-navy/40 hover:text-navy transition-colors"><Maximize2 className="w-4 h-4" /></button>
                <a href={url} target="_blank" rel="noreferrer" data-testid="aura-pres-open" title={lang === "en" ? "Open in new tab" : "Abrir en pestaña nueva"}
                  className="text-navy/40 hover:text-navy transition-colors"><ExternalLink className="w-4 h-4" /></a>
              </div>
            </div>
            <PdfViewer url={url} lang={lang} accent={accent} />
          </motion.div>
        )}
      </div>
      {full && sel && (
        <div className="fixed inset-0 z-[70] bg-navy/95 backdrop-blur-sm p-3 md:p-6 flex flex-col" data-testid="aura-pres-modal">
          <div className="flex items-center justify-between text-cream mb-3 px-1">
            <div className="font-medium truncate">{sel.title}</div>
            <button onClick={() => setFull(false)} data-testid="aura-pres-close" className="text-cream/70 hover:text-cream transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto rounded-lg bg-white">
            <PdfViewer url={url} lang={lang} accent={accent} />
          </div>
        </div>
      )}
    </section>
  );
}

function PdfViewer({ url, lang, accent }) {
  const [num, setNum] = useState(0);
  const [page, setPage] = useState(1);
  const [width, setWidth] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  useEffect(() => { setPage(1); setNum(0); }, [url]);
  return (
    <div ref={ref} className="relative bg-navy/[0.02]" data-testid="aura-pres-viewer">
      <Document file={url} onLoadSuccess={({ numPages }) => setNum(numPages)}
        loading={<div className="py-24 text-center text-navy/40 text-sm">{lang === "en" ? "Loading presentation…" : "Cargando presentación…"}</div>}
        error={<div className="py-24 text-center text-navy/40 text-sm">
          {lang === "en" ? "Could not load the PDF." : "No se pudo cargar el PDF."}{" "}
          <a className="underline" href={url} target="_blank" rel="noreferrer">{lang === "en" ? "Open it here" : "Ábrelo aquí"}</a>
        </div>}>
        <Page pageNumber={page} width={width || undefined} renderTextLayer={false} renderAnnotationLayer={false} />
      </Document>
      {num > 1 && (
        <div className="sticky bottom-4 flex justify-center pb-4 pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-1 rounded-full bg-navy text-cream shadow-lg px-2 py-1.5">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="aura-pdf-prev"
              className="p-1.5 rounded-full hover:bg-cream/10 disabled:opacity-30 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-xs px-2 tabular-nums" data-testid="aura-pdf-pagecount">{page} / {num}</span>
            <button onClick={() => setPage((p) => Math.min(num, p + 1))} disabled={page >= num} data-testid="aura-pdf-next"
              className="p-1.5 rounded-full hover:bg-cream/10 disabled:opacity-30 transition-colors"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
